// Lógica PURA de la tabla de cartera: filtrado, orden y los conteos de las pastillas. El corte en
// páginas vive en `components/portfolioTableModel.ts`, que es lo que leen los controles del pie.
// Recibe un arreglo de filas y devuelve otro — sin React y sin fetch. Cuando el endpoint de listado
// exista, esto no cambia: solo se le enchufa la fuente desde `api/portfolioSource`.
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { dayOffset, mxDay } from '@/lib/dateHelpers';
import { lockFilterBucketOf, lockReadingOf, type LockReading } from '../lockPhase';
import {
    ANY_FILTER,
    type ArrearsBucket,
    type PortfolioFilters,
    type PortfolioLockState,
    type PortfolioRow,
    type PortfolioSort,
    type PortfolioSortKey,
    type PortfolioStatKey,
    type PortfolioStats,
    type SortDirection,
} from './types';

/** Créditos que todavía deben dinero: los que pueden estar al día o en mora. */
const OPEN_STATUSES: readonly LoanCreditStatusEnum[] = [
    LoanCreditStatusEnum.PENDING_ACTIVATION,
    LoanCreditStatusEnum.ACTIVE,
    LoanCreditStatusEnum.IN_ARREARS,
];

/** Límites de cada tramo de mora del diseño, en días. */
const ARREARS_RANGE: Record<ArrearsBucket, { min: number; max: number }> = {
    none: { min: 0, max: 0 },
    '1-7': { min: 1, max: 7 },
    '8-30': { min: 8, max: 30 },
    '31-60': { min: 31, max: 60 },
    '61-90': { min: 61, max: 90 },
};

/** Una mora desconocida no cae en ningún tramo: `null` es «no se sabe», no es cero. */
export function matchesArrearsBucket(arrearsDays: number | null, bucket: ArrearsBucket): boolean {
    if (arrearsDays === null) return false;
    const range = ARREARS_RANGE[bucket];
    return arrearsDays >= range.min && arrearsDays <= range.max;
}

/**
 * Un saldo que no aplica no cae en ningún rango: `null` es «nunca contrajo la deuda», no es cero.
 * Sin ningún tope puesto el filtro no filtra, y esas filas siguen en la lista.
 */
export function matchesBalanceRange(
    outstandingBalanceCents: number | null,
    minCents: number | null,
    maxCents: number | null,
): boolean {
    if (minCents === null && maxCents === null) return true;
    if (outstandingBalanceCents === null) return false;
    if (minCents !== null && outstandingBalanceCents < minCents) return false;
    if (maxCents !== null && outstandingBalanceCents > maxCents) return false;
    return true;
}

/** El día de calendario en que se originó el crédito, en el piso de venta. */
function originationDay(row: PortfolioRow): string {
    return mxDay(row.createdAt);
}

function matchesSearch(row: PortfolioRow, search: string): boolean {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return (
        row.creditId.toLowerCase().includes(needle) ||
        (row.customerName?.toLowerCase().includes(needle) ?? false) ||
        (row.imei?.includes(needle) ?? false)
    );
}

/** La lectura del candado de la fila, con la hora del momento en que se pinta. */
export function rowLockReading(
    row: PortfolioRow,
    now: Date | string = new Date(),
): LockReading | null {
    return row.lockEvidence ? lockReadingOf(row.lockEvidence, now) : null;
}

/** Dónde cae la fila en el filtro de Bloqueo: la misma lectura que pinta la celda. */
function lockFilterStateOf(row: PortfolioRow, now: Date | string): PortfolioLockState | null {
    return row.lockEvidence ? lockFilterBucketOf(row.lockEvidence, now) : row.lockState;
}

export function filterPortfolio(
    rows: readonly PortfolioRow[],
    filters: PortfolioFilters,
    now: Date | string = new Date(),
): PortfolioRow[] {
    return rows.filter((row) => {
        if (!matchesSearch(row, filters.search)) return false;
        if (filters.levels.length > 0) {
            if (!row.clientLevel || !filters.levels.includes(row.clientLevel)) return false;
        }
        if (filters.status !== ANY_FILTER && row.status !== filters.status) return false;
        if (filters.lock !== ANY_FILTER && lockFilterStateOf(row, now) !== filters.lock) {
            return false;
        }
        if (filters.brand !== ANY_FILTER && row.productBrand !== filters.brand) return false;
        if (filters.store !== ANY_FILTER && row.storeName !== filters.store) return false;
        if (
            filters.arrears !== ANY_FILTER &&
            !matchesArrearsBucket(row.arrearsDays, filters.arrears)
        ) {
            return false;
        }
        if (
            !matchesBalanceRange(
                row.outstandingBalanceCents,
                filters.balanceMinCents,
                filters.balanceMaxCents,
            )
        ) {
            return false;
        }
        if (
            filters.paymentMinCents !== null &&
            row.weeklyPaymentWithIvaCents < filters.paymentMinCents
        ) {
            return false;
        }
        if (
            filters.paymentMaxCents !== null &&
            row.weeklyPaymentWithIvaCents > filters.paymentMaxCents
        ) {
            return false;
        }
        const day = originationDay(row);
        if (filters.fromDay && day < filters.fromDay) return false;
        if (filters.toDay && day > filters.toDay) return false;
        return true;
    });
}

/** El corte de UNA pastilla. «Al día» exige crédito vigente Y cero mora, como el diseño. */
export function applyStatFilter(
    rows: readonly PortfolioRow[],
    stat: PortfolioStatKey,
    now: Date | string = new Date(),
): PortfolioRow[] {
    switch (stat) {
        case 'all':
            return [...rows];
        case 'current':
            return rows.filter(
                (row) => OPEN_STATUSES.includes(row.status) && row.arrearsDays === 0,
            );
        case 'arrears':
            return rows.filter((row) => row.arrearsDays !== null && row.arrearsDays > 0);
        case 'settled':
            return rows.filter((row) => row.status === LoanCreditStatusEnum.SETTLED);
        case 'locked':
            // Solo el candado que el proveedor confirmó cuenta como bloqueado.
            return rows.filter((row) => lockFilterStateOf(row, now) === 'locked');
    }
}

/** `null` es «no hay fuente que contar»: las pastillas muestran «—», jamás un `0` inventado. */
export function computeStats(
    rows: readonly PortfolioRow[] | null,
    now: Date | string = new Date(),
): PortfolioStats {
    if (rows === null) {
        return { all: null, current: null, arrears: null, settled: null, locked: null };
    }
    // El conteo de bloqueados sale del MDM: sin leer o sin permiso, ese número no se sabe.
    const lockUnread = rows.some(
        (row) => row.lockState === 'unknown' || row.lockState === 'no-permission',
    );
    return {
        all: rows.length,
        current: applyStatFilter(rows, 'current').length,
        arrears: applyStatFilter(rows, 'arrears').length,
        settled: applyStatFilter(rows, 'settled').length,
        locked: lockUnread ? null : applyStatFilter(rows, 'locked', now).length,
    };
}

/** Jerarquía del nivel: el orden del negocio, no el alfabético. */
const LEVEL_ORDER: Record<ClientLevelEnum, number> = {
    [ClientLevelEnum.GOLD]: 0,
    [ClientLevelEnum.SILVER]: 1,
    [ClientLevelEnum.BRONZE]: 2,
    [ClientLevelEnum.NOT_ELIGIBLE]: 3,
};

/** Orden del ciclo de vida: lo vivo primero, lo terminal al final. */
const STATUS_ORDER: Record<LoanCreditStatusEnum, number> = {
    [LoanCreditStatusEnum.ACTIVE]: 0,
    [LoanCreditStatusEnum.IN_ARREARS]: 1,
    [LoanCreditStatusEnum.PENDING_ACTIVATION]: 2,
    [LoanCreditStatusEnum.SETTLED]: 3,
    [LoanCreditStatusEnum.WRITTEN_OFF]: 4,
    [LoanCreditStatusEnum.CANCELLED]: 5,
    [LoanCreditStatusEnum.DISCARDED]: 6,
};

const LOCK_ORDER: Record<PortfolioLockState, number> = {
    locked: 0,
    unlocked: 1,
    'no-device': 2,
    'no-permission': 3,
    unknown: 4,
};

/** Lo ausente va al final del ascendente: una celda vacía no compite por el primer lugar. */
function compareText(a: string | undefined, b: string | undefined): number {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b, 'es-MX');
}

/** Lo mismo para los números: `null` es «no se sabe» y se va al final, no a la cabeza. */
function compareNumber(a: number | null, b: number | null): number {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
}

function progressOf(row: PortfolioRow): number {
    return row.termWeeks > 0 ? row.paidInstallments / row.termWeeks : 0;
}

/** Lo que ordena la columna de saldo: la deuda total si el silo la publica; si no, el capital. */
export function balanceSortValue(row: PortfolioRow): number | null {
    return row.totalDebtCents === undefined ? row.outstandingBalanceCents : row.totalDebtCents;
}

/** Instante de originación en ms. Una fecha ilegible vale 0 y se hunde al final del descendente. */
function originationTime(row: PortfolioRow): number {
    const ms = Date.parse(row.createdAt);
    return Number.isNaN(ms) ? 0 : ms;
}

/** Los numéricos arrancan de mayor a menor: lo interesante está en el extremo alto. */
const DEFAULT_DIRECTION: Record<PortfolioSortKey, SortDirection> = {
    origination: 'desc',
    credit: 'asc',
    level: 'asc',
    customer: 'asc',
    product: 'asc',
    store: 'asc',
    payment: 'desc',
    progress: 'desc',
    balance: 'desc',
    status: 'asc',
    arrears: 'desc',
    lock: 'asc',
};

/** Click en un encabezado: alterna si ya ordenaba por esa columna, si no estrena su default. */
export function nextSort(current: PortfolioSort, key: PortfolioSortKey): PortfolioSort {
    if (current.key !== key) return { key, direction: DEFAULT_DIRECTION[key] };
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

function compareBy(key: PortfolioSortKey, a: PortfolioRow, b: PortfolioRow): number {
    switch (key) {
        case 'origination':
            return originationTime(a) - originationTime(b);
        case 'credit':
            return compareText(a.creditId, b.creditId);
        case 'level':
            return (
                (a.clientLevel ? LEVEL_ORDER[a.clientLevel] : 99) -
                (b.clientLevel ? LEVEL_ORDER[b.clientLevel] : 99)
            );
        case 'customer':
            return compareText(a.customerName, b.customerName);
        case 'product':
            return compareText(a.productLabel, b.productLabel);
        case 'store':
            return compareText(a.storeName, b.storeName);
        case 'payment':
            return a.weeklyPaymentWithIvaCents - b.weeklyPaymentWithIvaCents;
        case 'progress':
            return progressOf(a) - progressOf(b);
        case 'balance':
            return compareNumber(balanceSortValue(a), balanceSortValue(b));
        case 'status':
            return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        case 'arrears':
            return compareNumber(a.arrearsDays, b.arrearsDays);
        case 'lock':
            return LOCK_ORDER[a.lockState] - LOCK_ORDER[b.lockState];
    }
}

/** Las dos columnas cuyo dato puede faltar. Las demás siempre traen número. */
const NULLABLE_SORT_VALUE: Partial<Record<PortfolioSortKey, (row: PortfolioRow) => number | null>> =
    {
        arrears: (row) => row.arrearsDays,
        balance: balanceSortValue,
    };

export function sortPortfolio(rows: readonly PortfolioRow[], sort: PortfolioSort): PortfolioRow[] {
    const factor = sort.direction === 'asc' ? 1 : -1;
    const nullableValueOf = NULLABLE_SORT_VALUE[sort.key];
    // Lo ausente queda al final SIEMPRE: invertir el factor lo subiría a la cabeza en el
    // descendente, y ni «no se sabe» es la mora más grande ni «no aplica» el saldo más grande.
    return [...rows].sort((a, b) => {
        if (nullableValueOf) {
            const left = nullableValueOf(a);
            const right = nullableValueOf(b);
            if (left === null || right === null) return compareNumber(left, right);
        }
        return factor * compareBy(sort.key, a, b);
    });
}

/** Valores únicos y ordenados de una columna, para armar su combo de filtro. */
function distinct(values: readonly (string | undefined)[]): string[] {
    return [...new Set(values.filter((value): value is string => Boolean(value)))].sort((a, b) =>
        a.localeCompare(b, 'es-MX'),
    );
}

/**
 * Las marcas del combo de Producto, no los modelos: el mock filtra por marca. La fila sin contexto
 * de venta resuelto no tiene marca y ningún filtro la alcanza.
 */
export function listBrands(rows: readonly PortfolioRow[]): string[] {
    return distinct(rows.map((row) => row.productBrand));
}

/**
 * Solo las tiendas con nombre del padrón. El combo del mock se arma del padrón, no de las filas: la
 * fila que cayó al id crudo no es alcanzable por aquí, y se busca por el buscador.
 */
export function listStores(rows: readonly PortfolioRow[]): string[] {
    return distinct(rows.map((row) => (row.storeNameResolved ? row.storeName : undefined)));
}

/** Cuántos filtros hay puestos. Los chips de Nivel cuentan uno por chip, como en el diseño. */
export function activeFilterCount(filters: PortfolioFilters): number {
    let total = filters.levels.length;
    if (filters.search.trim()) total += 1;
    if (filters.status !== ANY_FILTER) total += 1;
    if (filters.lock !== ANY_FILTER) total += 1;
    if (filters.brand !== ANY_FILTER) total += 1;
    if (filters.store !== ANY_FILTER) total += 1;
    if (filters.arrears !== ANY_FILTER) total += 1;
    if (filters.balanceMinCents !== null) total += 1;
    if (filters.balanceMaxCents !== null) total += 1;
    if (filters.paymentMinCents !== null) total += 1;
    if (filters.paymentMaxCents !== null) total += 1;
    if (filters.fromDay) total += 1;
    if (filters.toDay) total += 1;
    return total;
}

/**
 * Los atajos Hoy/7d/30d/Trim/Todo. `days` negativo limpia el rango.
 * `today` entra por parámetro para que el cálculo sea el mismo en la pantalla y en el test.
 */
export function dateShortcutRange(
    days: number,
    today: string = mxDay(new Date().toISOString()),
): { fromDay: string; toDay: string } {
    if (days < 0) return { fromDay: '', toDay: '' };
    return { fromDay: dayOffset(today, -days), toDay: today };
}
